import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "./LocaleLink";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, ChevronDown, ChevronRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { LANGS, CONTACT, waHref } from "../i18n/translations";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "./ui/dropdown-menu";

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);
  const location = useLocation();
  const waLink = waHref(t("contact.whatsappText"));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileGuidesOpen(false);
  }, [location.pathname]);

  const guideLinks = [
    { to: "/airbnb-management-nice", label: "Airbnb Management Nice" },
    { to: "/trusted-airbnb-management-nice", label: "Trusted Airbnb Management" },
    { to: "/property-management-french-riviera", label: "Property Management Riviera" },
    { to: "/concierge-services-nice", label: "Concierge Services Nice" },
    { to: "/luxury-property-management-nice", label: "Luxury Property Management" },
    { to: "/english-speaking-agency-nice", label: "English-Speaking Agency" },
    { to: "/buying-property-nice-american", label: "Buying in Nice (Americans)" },
    { to: "/buying-property-france-turkish", label: "Buying in France (Turkish)" },
    { to: "/french-riviera-investment-guide", label: "Investment Guide" },
    { to: "/living-in-nice-american", label: "Living in Nice" },
    { to: "/retire-in-nice", label: "Retire in Nice" },
    { to: "/relocating-to-nice-from-us", label: "Relocating from the US" },
  ];

  const navItems = [
    { to: "/", label: t("nav.home") },
    { to: "/properties", label: t("nav.properties") },
    { to: "https://velmiraliving.guestybookings.com/en", label: t("nav.shortTerm"), external: true },
    { to: "/property-management", label: t("nav.propertyMgmt") },
    { type: "dropdown", label: t("nav.guides"), items: guideLinks },
    { to: "/blog", label: t("nav.blog") },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-background/80 border-b border-border/60 py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src="/logo-mark.png"
            alt="Velmira Living"
            data-testid="logo-img"
            className="h-10 w-10 rounded-[3px] shrink-0"
          />
          <span className="flex flex-col leading-none">
            <span className="font-headings text-2xl tracking-wide text-foreground">Velmira</span>
            <span lang="en" className="brand-caps font-accent text-[10px] uppercase tracking-[0.35em] text-primary -mt-0.5">Living</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) =>
            item.type === "dropdown" ? (
              <DropdownMenu key={item.label}>
                <DropdownMenuTrigger data-testid="nav-guides-trigger" className="flex items-center gap-1 font-body text-sm text-foreground/80 hover:text-primary transition-colors duration-300 tracking-wide focus:outline-none">
                  {item.label} <ChevronDown size={14} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-popover border-border w-64">
                  {item.items.map((g) => (
                    <DropdownMenuItem key={g.to} asChild>
                      <Link to={g.to} data-testid={`guide-link-${g.to.slice(1)}`} className="font-body text-sm text-foreground/85 cursor-pointer">{g.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : item.external ? (
              <a key={item.to} href={item.to} target="_blank" rel="noopener noreferrer" data-testid={`nav-${item.label}`} className="font-body text-sm text-foreground/80 hover:text-primary transition-colors duration-300 tracking-wide">
                {item.label}
              </a>
            ) : (
              <HashLink key={item.to} to={item.to} className="font-body text-sm text-foreground/80 hover:text-primary transition-colors duration-300 tracking-wide" testid={`nav-${item.label}`}>
                {item.label}
              </HashLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <div className="flex items-center gap-1" data-testid="lang-switcher">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-2 py-1 text-xs tracking-widest transition-colors duration-300 ${
                  lang === l.code ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <a
            data-testid="whatsapp-cta-header"
            href={waLink}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="flex items-center gap-2 border border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground px-5 py-2.5 uppercase tracking-widest text-xs transition-all duration-300"
          >
            <MessageCircle size={15} /> {t("nav.whatsapp")}
          </a>
        </div>

        <button data-testid="mobile-menu-toggle" className="lg:hidden text-foreground" onClick={() => setOpen((v) => !v)} aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-background/95 border-t border-border/60"
          >
            <div
              data-testid="mobile-menu-scroll"
              className="px-6 py-6 flex flex-col gap-5 max-h-[calc(100vh-88px)] overflow-y-auto overscroll-contain"
            >
              {navItems.map((item) =>
                item.type === "dropdown" ? (
                  <div key={item.label} className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => setMobileGuidesOpen((v) => !v)}
                      aria-expanded={mobileGuidesOpen}
                      data-testid="mobile-guides-toggle"
                      className="flex items-center justify-between font-headings text-2xl text-foreground/90 w-full text-left py-1"
                    >
                      <span>{item.label}</span>
                      <ChevronRight
                        size={20}
                        className={`text-primary transition-transform duration-300 ${mobileGuidesOpen ? "rotate-90" : ""}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {mobileGuidesOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-3 pl-4 flex flex-col gap-3 border-l border-primary/30">
                            {item.items.map((g) => (
                              <Link
                                key={g.to}
                                to={g.to}
                                data-testid={`mobile-guide-${g.to.slice(1)}`}
                                className="font-body text-base text-foreground/75 hover:text-primary transition-colors"
                              >
                                {g.label}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : item.external ? (
                  <a
                    key={item.to}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-nav-${item.label}`}
                    className="font-headings text-2xl text-foreground/90 py-1"
                  >
                    {item.label}
                  </a>
                ) : (
                  <HashLink
                    key={item.to}
                    to={item.to}
                    className="font-headings text-2xl text-foreground/90 py-1"
                    testid={`mobile-nav-${item.label}`}
                  >
                    {item.label}
                  </HashLink>
                )
              )}
              <div className="flex items-center gap-3 pt-4 border-t border-border/40">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`mobile-lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`px-3 py-1.5 text-sm tracking-widest ${lang === l.code ? "text-primary" : "text-muted-foreground"}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <a
                href={waLink}
                target="_blank"
                rel="nofollow noopener noreferrer"
                data-testid="whatsapp-cta-mobile"
                className="flex items-center justify-center gap-2 border border-primary text-primary px-5 py-3 uppercase tracking-widest text-xs"
              >
                <MessageCircle size={15} /> {t("nav.whatsapp")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Supports "/#anchor" links from any route
const HashLink = ({ to, children, className, testid }) => {
  const isHash = to.includes("#");
  if (isHash) {
    const [path, hash] = to.split("#");
    const handle = (e) => {
      if (window.location.pathname === (path || "/")) {
        e.preventDefault();
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    };
    return (
      <Link to={to} onClick={handle} className={className} data-testid={testid}>
        {children}
      </Link>
    );
  }
  return (
    <Link to={to} className={className} data-testid={testid}>
      {children}
    </Link>
  );
};

export default Header;
